import React, { useState } from 'react';
import { Layout, Menu, Button, Space, Breadcrumb } from 'antd';
import {
  LogoutOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HistoryOutlined,
  CrownOutlined,
  FileTextOutlined,
  BookOutlined,
  ProjectOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthService } from '../../services';
import TokenManager from '../../utils/tokenManager';
import ErrorHandler from '../../utils/errorHandler';
import '../../styles/MainLayout.scss';

const { Header, Sider, Content } = Layout;

// 菜单配置
const menuItems = [
  {
    key: 'users',
    path: '/users',
    icon: <TeamOutlined />,
    label: '账号管理',
  },
  {
    key: 'login-records',
    path: '/login-records',
    icon: <HistoryOutlined />,
    label: '登录记录',
  },
  {
    key: 'token-config',
    path: '/token-config',
    icon: <CrownOutlined />,
    label: 'Token配置',
  },
  {
    key: 'token-records',
    path: '/token-records',
    icon: <FileTextOutlined />,
    label: 'Token记录',
  },
  {
    key: 'tasks',
    path: '/tasks',
    icon: <ProjectOutlined />,
    label: '任务管理',
  },
  {
    key: 'dict',
    path: '/dict',
    icon: <BookOutlined />,
    label: '字典管理',
  },
];

// 面包屑映射配置
const breadcrumbMap: Record<string, string[]> = {
  '/users': ['首页', '账号管理'],
  '/login-records': ['首页', '登录记录'],
  '/token-config': ['首页', 'Token配置'],
  '/token-records': ['首页', 'Token记录'],
  '/tasks': ['首页', '任务管理'],
  '/dict': ['首页', '字典管理'],
};

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, title }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 处理登出
  const handleLogout = async () => {
    try {
      // 调用登出 API
      await AuthService.logout();
    } catch (error) {
      ErrorHandler.handleApiError(error, false);
    } finally {
      // 清除本地存储的 token
      TokenManager.clearToken();
      navigate('/login');
    }
  };

  // 获取当前路径的面包屑
  const getBreadcrumbs = () => {
    const breadcrumbItems = breadcrumbMap[location.pathname] || ['首页'];
    
    return (
      <Breadcrumb style={{ margin: '16px 0' }}>
        {breadcrumbItems.map((item, index) => (
          <Breadcrumb.Item key={index}>{item}</Breadcrumb.Item>
        ))}
      </Breadcrumb>
    );
  };

  // 获取当前选中的菜单项
  const getSelectedKey = () => {
    // 直接从菜单配置中查找匹配的路径
    const currentMenu = menuItems.find(item => item.path === location.pathname);
    return currentMenu ? currentMenu.key : 'users';
  };

  return (
    <Layout className="main-layout" style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        className="main-sider"
        trigger={null} // 移除默认的触发器
        style={{ overflow: 'auto', height: '100vh', position: 'fixed', left: 0 }}
      >
        <div className="logo">
          {collapsed ? <img style={{
            width: 50,
            height: 50
          }} src="https://static.pqmagic.cn/magic-web/img/icon.png" alt="" /> : <span>后台管理系统</span>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems.map(item => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
            onClick: () => navigate(item.path),
          }))}
        />
      </Sider>
      
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'all 0.2s' }}>
        <Header className="main-header" style={{ position: 'sticky', top: 0, zIndex: 1, width: '100%' }}>
          <div className="header-content">
            <div className="header-left">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                className="trigger-btn"
              />
              <h1>{title || '管理系统'}</h1>
              {getBreadcrumbs()}
            </div>
            <div className="header-right">
              <Space>
                <Button
                  type="link"
                  icon={<LogoutOutlined />}
                  onClick={handleLogout}
                  className="logout-btn"
                >
                  注销
                </Button>
              </Space>
            </div>
          </div>
        </Header>
        
        <Content className="main-content" style={{ overflow: 'auto', padding: '24px', height: 'calc(100vh - 64px)' }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
